import type { z } from 'zod';

import type {
    BaseNavigationScreenSchema,
    NavigationAccountDeveloperTabSchema,
    NavigationOrgSettingsAgentsTabSchema,
    NavigationOrgSettingsMainTabSchema,
    NavigationOrgSettingsPlansTabSchema,
    NavigationRevisionSchema,
    NavigationScreenAccountSettingsSchema,
    NavigationScreenAgentStateSchema,
    NavigationScreenAgentsStateSchema,
    NavigationScreenChangeRequestsFiltersSchema,
    NavigationScreenChangeRequestsStateSchema,
    NavigationScreenCollectionStateSchema,
    NavigationScreenContentGapsStateSchema,
    NavigationScreenErrorNotFoundSchema,
    NavigationScreenHomeStateSchema,
    NavigationScreenJoinSchema,
    NavigationScreenLegacyIntegrationsSchema,
    NavigationScreenLinkAuditStateSchema,
    NavigationScreenOAuthAuthorizeSchema,
    NavigationScreenOrganizationContentStateSchema,
    NavigationScreenOrgInviteSchema,
    NavigationScreenOrgJoinSchema,
    NavigationScreenOrgSettingsSchema,
    NavigationScreenOrgWithBaseNameSchema,
    NavigationScreenRootSchema,
    NavigationScreenSiteFindingsFiltersSchema,
    NavigationScreenSiteInviteSchema,
    NavigationScreenSitePreviewAuthSchema,
    NavigationScreenSiteScansFiltersSchema,
    NavigationScreenSiteStateSchema,
    NavigationScreenSiteStructureStateSchema,
    NavigationScreenSitesListStateSchema,
    NavigationScreenSiteTabAnalyticsSchema,
    NavigationScreenSiteTabCustomizationSchema,
    NavigationScreenSiteTabFindingsSchema,
    NavigationScreenSiteTabMembersSchema,
    NavigationScreenSiteTabPreviewSchema,
    NavigationScreenSiteTabSchema,
    NavigationScreenSiteTabSettingsSchema,
    NavigationScreenSiteTabStructureSchema,
    NavigationScreenSiteTabTriageSchema,
    NavigationScreenSpaceChangeRequestStateSchema,
    NavigationScreenSpaceRevisionStateSchema,
    NavigationScreenSpaceStateSchema,
    NavigationScreenStateSchema,
    NavigationScreenStyleguideNewStateSchema,
    NavigationScreenStyleguidesStateSchema,
    NavigationScreenTrashStateSchema,
    NavigationScreenTriageStateSchema,
    NavigationScreenUserStateSchema,
    NavigationScreenWelcomeSchema,
} from './schema';
import {
    NavigationScreenSiteFindingsDateRangeValues,
    NavigationSiteCustomizationTabsValues,
    NavigationSiteInsightsReportsValues,
    NavigationSiteSettingsSectionsValues,
} from './schema';
import type {
    NavigationIntegrationsModal,
    NavigationSiteSideSheet,
    NavigationSpaceSideSheet,
} from './side-sheets';

export interface LocationDescriptor {
    pathname: string;
    search?: string;
    hash?: string;
}

export type NavigationScreenState = z.infer<typeof NavigationScreenStateSchema>;

export type BaseNavigationScreen = z.infer<typeof BaseNavigationScreenSchema>;

/**
 * Screen when url cannot be resolved.
 */
export type NavigationScreenErrorNotFound = z.infer<typeof NavigationScreenErrorNotFoundSchema>;

/**
 * Screen for showing the sign up page, which is very similar to the sign in page, but
 * has a style more geared towards encouraging users to join GitBook.
 */
export type NavigationScreenJoin = z.infer<typeof NavigationScreenJoinSchema>;

/**
 * Main screen when opening the app or an organization.
 * It redirects to the latest visited content.
 */
export type NavigationScreenRoot = z.infer<typeof NavigationScreenRootSchema>;

/**
 * Main screen displaying the content for the org or user.
 */
export type NavigationScreenHomeState = z.infer<typeof NavigationScreenHomeStateSchema>;

/**
 * Consent screen for completing a GitBook API OAuth authorization request.
 */
export type NavigationScreenOAuthAuthorize = z.infer<typeof NavigationScreenOAuthAuthorizeSchema>;

/**
 * Info screen on a user
 */
export type NavigationScreenUserState = z.infer<typeof NavigationScreenUserStateSchema>;

/**
 * State of the navigation for onboarding and when creating an org
 */
export type NavigationScreenWelcome = z.infer<typeof NavigationScreenWelcomeSchema>;

/**
 * State of the navigation when viewing the integrations modal in a space
 */
export type NavigationScreenSpaceIntegrationsModalState = Omit<
    NavigationScreenSpaceState,
    'sideSheet'
> & {
    sideSheet: NavigationIntegrationsModal;
};

/**
 * State of the navigation when viewing a revision in a space (including the primary content).
 */
export type NavigationScreenSpaceRevisionState = z.infer<
    typeof NavigationScreenSpaceRevisionStateSchema
>;

export type NavigationRevision = z.infer<typeof NavigationRevisionSchema>;

/**
 * State of the navigation when viewing a change request in a space.
 */
export type NavigationScreenSpaceChangeRequestState = z.infer<
    typeof NavigationScreenSpaceChangeRequestStateSchema
>;

export function isNavigationScreenSpaceChangeRequestState(
    screen: NavigationScreenState
): screen is NavigationScreenSpaceChangeRequestState {
    return (
        screen.type === 'space' && 'changeRequest' in screen && screen.changeRequest !== undefined
    );
}

/**
 * State of the navigation when viewing a space.
 */
export type NavigationScreenSpaceState = z.infer<typeof NavigationScreenSpaceStateSchema>;

/**
 * State of the navigation when viewing a collection.
 */
export type NavigationScreenCollectionState = z.infer<typeof NavigationScreenCollectionStateSchema>;

/**
 * State of the navigation when viewing a site.
 *
 * Tabs: overview, analytics, settings, customization, preview, structure.
 */

export type NavigationScreenSiteState = z.infer<typeof NavigationScreenSiteStateSchema>;

/**
 * State of the navigation when viewing the full-screen triage experience for a site.
 */
export type NavigationScreenTriageState = z.infer<typeof NavigationScreenTriageStateSchema>;

/**
 * State of the navigation when viewing the content gaps screen for a site.
 */
export type NavigationScreenContentGapsState = z.infer<
    typeof NavigationScreenContentGapsStateSchema
>;

/**
 * State of the navigation when viewing the link audit screen for a site.
 */
export type NavigationScreenLinkAuditState = z.infer<typeof NavigationScreenLinkAuditStateSchema>;

/**
 * State of the navigation when viewing the site structure screen.
 */
export type NavigationScreenSiteStructureState = z.infer<
    typeof NavigationScreenSiteStructureStateSchema
>;

/**
 * State of the navigation when viewing the integrations modal in a site
 */
export type NavigationScreenSiteIntegrationsModalState = Omit<
    NavigationScreenSiteState,
    'sideSheet'
> & {
    sideSheet: NavigationIntegrationsModal;
};

export type NavigationScreenSiteTab = z.infer<typeof NavigationScreenSiteTabSchema>;

export const NavigationSiteInsightsReports = NavigationSiteInsightsReportsValues;

export type NavigationScreenSiteTabAnalytics = z.infer<
    typeof NavigationScreenSiteTabAnalyticsSchema
>;

/**
 * Filters applied to the site findings list.
 */
export type NavigationScreenSiteFindingsFilters = z.infer<
    typeof NavigationScreenSiteFindingsFiltersSchema
>;

export const NavigationScreenSiteFindingsDateRanges = NavigationScreenSiteFindingsDateRangeValues;

/** A relative activity-window preset (by last-updated) applied to the findings list. */
export type NavigationScreenSiteFindingsDateRange =
    (typeof NavigationScreenSiteFindingsDateRangeValues)[number];

/**
 * Internal findings tab type used by triage mode.
 */
export type NavigationScreenSiteTabFindings = z.infer<typeof NavigationScreenSiteTabFindingsSchema>;

/**
 * State of the triage inbox tab — shows open ContentGap findings.
 */
export type NavigationScreenSiteTabTriage = z.infer<typeof NavigationScreenSiteTabTriageSchema>;

export type NavigationScreenSiteScansFilters = z.infer<
    typeof NavigationScreenSiteScansFiltersSchema
>;

export const NavigationSiteSettingsSections = NavigationSiteSettingsSectionsValues;

export type NavigationSiteSettingsSection = (typeof NavigationSiteSettingsSections)[number];

export const NavigationSiteCustomizationTabs = NavigationSiteCustomizationTabsValues;

export type NavigationScreenSiteTabSettings = z.infer<typeof NavigationScreenSiteTabSettingsSchema>;

export type NavigationScreenSiteTabPreview = z.infer<typeof NavigationScreenSiteTabPreviewSchema>;

export type NavigationScreenSiteTabStructure = z.infer<
    typeof NavigationScreenSiteTabStructureSchema
>;

export type NavigationScreenSiteTabMembers = z.infer<typeof NavigationScreenSiteTabMembersSchema>;

export type NavigationScreenSiteTabCustomization = z.infer<
    typeof NavigationScreenSiteTabCustomizationSchema
>;

/**
 * State of the navigation when viewing the screen listing sites.
 */
export type NavigationScreenSitesListState = z.infer<typeof NavigationScreenSitesListStateSchema>;

/**
 * State of the navigation when sharing invites.
 */
export type NavigationScreenOrgInvite = z.infer<typeof NavigationScreenOrgInviteSchema>;

/**
 * State of the navigation when sharing/inviting members to a site.
 */
export type NavigationScreenSiteInvite = z.infer<typeof NavigationScreenSiteInviteSchema>;

/**
 * State of the navigation when opening a login link with the org basename in it.
 * These urls are mostly legacy.
 */
export type NavigationScreenOrgWithBaseName = z.infer<typeof NavigationScreenOrgWithBaseNameSchema>;

/**
 * State of the navigation when viewing an invite.
 */
export type NavigationScreenOrgJoin = z.infer<typeof NavigationScreenOrgJoinSchema>;

export type NavigationOrgSettingsPlansTab = z.infer<typeof NavigationOrgSettingsPlansTabSchema>;

export type NavigationOrgSettingsAgentsTab = z.infer<typeof NavigationOrgSettingsAgentsTabSchema>;

export type NavigationOrgSettingsMainTab = z.infer<typeof NavigationOrgSettingsMainTabSchema>;

export type NavigationScreenOrgSettings = z.infer<typeof NavigationScreenOrgSettingsSchema>;

/** Legacy organization-agnostic integrations URLs that redirect to organization settings. */
export type NavigationScreenLegacyIntegrationsState = z.infer<
    typeof NavigationScreenLegacyIntegrationsSchema
>;

/**
 * State of the navigation when viewing organization settings.
 */
export type NavigationScreenOrgSettingsMain = Omit<NavigationScreenOrgSettings, 'tab'> & {
    tab: NavigationOrgSettingsMainTab;
};

export type NavigationAccountSettingsTab = NavigationScreenAccountSettings['tab'];
export type NavigationAccountDeveloperTab = z.infer<typeof NavigationAccountDeveloperTabSchema>;

/**
 * State of the navigation when viewing account settings.
 */
export type NavigationScreenAccountSettings = z.infer<typeof NavigationScreenAccountSettingsSchema>;

/**
 * State for viewing and managing the trash content.
 */
export type NavigationScreenTrashState = z.infer<typeof NavigationScreenTrashStateSchema>;

/**
 * Filters that can be applied to the list of change requests.
 */
export type NavigationScreenChangeRequestsFilters = z.infer<
    typeof NavigationScreenChangeRequestsFiltersSchema
>;

/**
 * State for the organization change requests screen.
 */
export type NavigationScreenChangeRequestsState = z.infer<
    typeof NavigationScreenChangeRequestsStateSchema
>;

/**
 * State for an organization agent conversation shown with a site preview.
 */
export type NavigationScreenAgentState = z.infer<typeof NavigationScreenAgentStateSchema>;

/**
 * State for the agent screen.
 */
export type NavigationScreenAgentsState = z.infer<typeof NavigationScreenAgentsStateSchema>;

/**
 * State for the organization content screen.
 */
export type NavigationScreenOrganizationContentState = z.infer<
    typeof NavigationScreenOrganizationContentStateSchema
>;

/**
 * State for the organization style guides screen.
 */
export type NavigationScreenStyleguidesState = z.infer<
    typeof NavigationScreenStyleguidesStateSchema
>;

/**
 * State for the full-screen style guide creation route of a site.
 */
export type NavigationScreenStyleguideNewState = z.infer<
    typeof NavigationScreenStyleguideNewStateSchema
>;

/**
 * State for the site preview auth screen, which is used when a user tries to access a site preview link and needs to authenticate first.
 */
export type NavigationScreenSitePreviewAuthState = z.infer<
    typeof NavigationScreenSitePreviewAuthSchema
>;

// Re-export these names for backward compatibility with existing imports.
export type { NavigationSiteSideSheet, NavigationSpaceSideSheet };
